import { Fragment, useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { createHealthPlanImport, saveHealthPlanIdentifierAssignments } from '../services/healthPlanImportsService';
import type {
  HealthPlanAuditItem,
  HealthPlanImportTotals,
  HealthPlanLifeRecord,
  HealthPlanMovementRecord,
  HealthPlanCopayRecord,
  HealthPlanProvider,
  HealthPlanSourceFile,
} from '../services/healthPlanTypes';
import { parseHealthPlanFiles } from '../services/healthPlanParser';
import { healthPlanProviders, healthPlanProviderBySlug } from './healthPlanProviders';
import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { useToast } from '@/hooks/useToast';
import { normalizeString } from '@/utils/stringUtils';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { isCompetenceConsolidated } from '../services/expensesConsolidationService';

type CollaboratorReference = {
  id: string;
  name: string;
  cpf?: string | null;
  normalizedName: string;
  normalizedPlainName: string;
  healthPlanIdentifiers?: Record<string, string> | null;
  isActive: boolean;
};
type MatchStatus = 'auto_cpf' | 'manual' | 'not_found';

type HealthPlanMatch = {
  record: HealthPlanLifeRecord;
  collaboratorId: string | null;
  status: MatchStatus;
};

const normalizeDigits = (value?: string | null): string => (value || '').replace(/\D/g, '');

const isPrimaryHolderRecord = (record: HealthPlanLifeRecord): boolean => {
  if (record.isPrimaryHolder != null) return record.isPrimaryHolder;
  const holderCpf = normalizeDigits(record.holderCpf);
  const beneficiaryCpf = normalizeDigits(record.beneficiaryCpf);
  const relationship = (record.relationship || '').toUpperCase();
  return !record.holderCpf || (holderCpf && beneficiaryCpf && holderCpf === beneficiaryCpf) || relationship.includes('TIT');
};

function formatCpf(cpf?: string | null): string {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

const formatCertificateCode = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes('/')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length <= 2) return trimmed;
  const prefix = digits.slice(0, -2);
  const suffix = digits.slice(-2);
  return `${prefix}/${suffix}`;
};

const formatCpfOrCode = (life?: HealthPlanLifeRecord | null): string => {
  if (!life) return '—';
  const normalizedCpf = (life.beneficiaryCpf || '').replace(/\D/g, '');
  if (normalizedCpf.length === 11) {
    return formatCpf(life.beneficiaryCpf);
  }
  const certificateLike =
    formatCertificateCode(life.certificateCode) || formatCertificateCode(life.userCode) || null;
  if (certificateLike) return certificateLike;
  const code =
    life.userCode ||
    life.certificateCode ||
    life.familyIdentifier ||
    (life.beneficiaryCpf ? life.beneficiaryCpf.trim() : '') ||
    life.holderCpf;
  return code && code.trim() ? code.trim() : '—';
};

const getValidProvider = (value: string | null): HealthPlanProvider | '' => {
  if (!value) return '';
  return healthPlanProviderBySlug[value] ? (value as HealthPlanProvider) : '';
};

function MappingCombobox({
  value,
  options,
  disabled,
  onChange,
}: {
  value?: string | null;
  options: CollaboratorReference[];
  disabled?: boolean;
  onChange: (id: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.id === value) ?? null;
  const deferredQuery = useDeferredValue(query);
  const filtered = useMemo(() => {
    const normalizedQuery = normalizeString(deferredQuery);
    if (!normalizedQuery) return options;
    return options.filter((opt) => opt.normalizedName.includes(normalizedQuery));
  }, [options, deferredQuery]);

  return (
    <Combobox value={selected} onChange={(opt) => onChange(opt?.id ?? null)} disabled={disabled}>
      {({ open }) => (
        <div className="relative w-full min-w-[16rem]">
          <Combobox.Input
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            displayValue={(opt: CollaboratorReference | null) => opt?.name ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            placeholder="Selecionar colaborador..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-500">Nenhum resultado</div>
              ) : (
                filtered.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={person}
                  >
                    {({ selected: isSelected, active }) => (
                      <>
                        <span className="block">
                          <span className={`block ${isSelected ? 'font-semibold text-gray-900' : 'font-normal text-gray-900'}`}>
                            {person.name}
                          </span>
                          {person.cpf ? (
                            <span className="block text-xs text-gray-500">{formatCpf(person.cpf)}</span>
                          ) : null}
                        </span>
                        {isSelected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                              }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

function StatusBadge({ status }: { status: MatchStatus }) {
  const statusMap: Record<
    MatchStatus,
    { text: string; className: string; Icon: typeof CheckCircleIcon }
  > = {
    auto_cpf: { text: 'Auto (Histórico/Nome)', className: 'bg-green-100 text-green-700', Icon: CheckCircleIcon },
    manual: { text: 'Manual', className: 'bg-blue-100 text-blue-700', Icon: CheckCircleIcon },
    not_found: { text: 'Sem match', className: 'bg-amber-100 text-amber-700', Icon: ExclamationTriangleIcon },
  };
  const data = statusMap[status];
  const Icon = data.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${data.className}`}>
      <Icon className="h-4 w-4" aria-hidden />
      {data.text}
    </span>
  );
}

export function HealthPlanImportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryProvider = getValidProvider(searchParams.get('provider'));
  const [selectedProvider, setSelectedProvider] = useState<HealthPlanProvider | ''>(queryProvider);
  const [manualIof, setManualIof] = useState('');
  useEffect(() => {
    if (!queryProvider) return;
    setSelectedProvider((current) => (current ? current : queryProvider));
  }, [queryProvider]);
  const providerInfo = selectedProvider ? healthPlanProviderBySlug[selectedProvider] : null;
  const { user } = useAuth();
  const { success: showSuccess, error: showError } = useToast();
  const { data: collaborators, isLoading: loadingCollaborators } = useCollaboratorsRealtimeQuery();
  const collaboratorRefs = useMemo<CollaboratorReference[]>(() => {
    return (collaborators || []).map((c) => {
      const name = c.name ?? 'Sem nome';
      const cpf = (c as any).cpf ?? null;
      return {
        id: c.id ?? '',
        name,
        cpf,
        normalizedName: normalizeString(`${name} ${cpf ?? ''}`),
        normalizedPlainName: normalizeString(name),
        healthPlanIdentifiers: (c as any).healthPlanIdentifiers ?? null,
        isActive: (c as any).status !== 'desligado' && (c as any).status !== 'inactive',
      };
    });
  }, [collaborators]);

  const collaboratorByPlainName = useMemo(() => {
    const map = new Map<string, CollaboratorReference>();
    collaboratorRefs.forEach((collab) => {
      if (!collab.normalizedPlainName) return;
      if (!map.has(collab.normalizedPlainName)) {
        map.set(collab.normalizedPlainName, collab);
      }
    });
    return map;
  }, [collaboratorRefs]);

  const collaboratorByIdentifier = useMemo(() => {
    if (!selectedProvider) return new Map<string, CollaboratorReference>();
    const map = new Map<string, CollaboratorReference>();
    collaboratorRefs.forEach((collab) => {
      const rawIdentifier = collab.healthPlanIdentifiers?.[selectedProvider] ?? null;
      if (!rawIdentifier) return;
      const normalized = rawIdentifier.replace(/\D/g, '');
      if (normalized) {
        map.set(normalized, collab);
      }
    });
    return map;
  }, [collaboratorRefs, selectedProvider]);

  const [billingPeriod, setBillingPeriod] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [matches, setMatches] = useState<HealthPlanMatch[]>([]);
  const [movements, setMovements] = useState<HealthPlanMovementRecord[]>([]);
  const [copayEntries, setCopayEntries] = useState<HealthPlanCopayRecord[]>([]);
  const [copayRawText, setCopayRawText] = useState<string[]>([]);
  const [totals, setTotals] = useState<HealthPlanImportTotals>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [pdfPassword, setPdfPassword] = useState('');

  const sourceFiles: HealthPlanSourceFile[] = useMemo(
    () =>
      files.map((file) => ({
        name: file.name,
        size: file.size,
        contentType: file.type || null,
      })),
    [files],
  );

  const copayByCpf = useMemo(() => {
    const map = new Map<string, number>();
    const add = (key: string, amount: number | null | undefined) => {
      if (!key || key.length < 3 || amount == null) return;
      map.set(key, (map.get(key) ?? 0) + amount);
    };

    copayEntries.forEach((entry) => {
      const cpf = (entry.beneficiaryCpf || entry.holderCpf || '').replace(/\D/g, '');
      const nameKey = normalizeString(entry.beneficiaryName || '');
      const holderNameKey = normalizeString(entry.holderName || '');
      const holderCpf = (entry.holderCpf || '').replace(/\D/g, '');

      add(cpf, entry.value);
      add(nameKey, entry.value);
      add(holderCpf, entry.value);
      add(holderNameKey, entry.value);

      if (entry.userCode) {
        add(entry.userCode, entry.value);
      }
      if (entry.holderUserCode) {
        add(entry.holderUserCode, entry.value);
      }
    });
    return map;
  }, [copayEntries]);

  const attachCopayToLives = useMemo(
    () =>
      (lives: HealthPlanLifeRecord[], copays: HealthPlanCopayRecord[]) => {
        const clone = lives.map((life) => ({
          ...life,
          copayAmount: life.copayAmount ?? 0,
          copayEntries: life.copayEntries ? [...life.copayEntries] : [],
        }));
        const normalizeCpfKey = (value?: string | null) => (value || '').replace(/\D/g, '');
        const isHolderRecord = (life?: HealthPlanLifeRecord | null) => {
          if (!life) return false;
          const relationship = (life.relationship || '').toUpperCase();
          if (relationship.includes('TIT')) return true;
          const beneficiaryCpf = normalizeCpfKey(life.beneficiaryCpf);
          const holderCpf = normalizeCpfKey(life.holderCpf);
          return Boolean(beneficiaryCpf && holderCpf && beneficiaryCpf === holderCpf);
        };

        // Helper to remove trailing numbers from name (e.g. "NAME 123" -> "NAME")
        const cleanName = (name: string | null | undefined) => {
          if (!name) return '';
          return name.replace(/\s+\d+$/, '').trim();
        };

        const findTarget = (entry: HealthPlanCopayRecord) => {
          const cpf = normalizeCpfKey(entry.beneficiaryCpf);
          const holderCpf = normalizeCpfKey(entry.holderCpf);
          const nameKey = normalizeString(entry.beneficiaryName || '');
          const holderNameKey = normalizeString(entry.holderName || '');
          const cleanedHolderNameKey = normalizeString(cleanName(entry.holderName));

          const byUserCode = entry.userCode ? clone.find((life) => life.userCode === entry.userCode) : undefined;

          const byCpf =
            clone.find((life) => normalizeCpfKey(life.beneficiaryCpf) === cpf) ??
            clone.find((life) => normalizeCpfKey(life.holderCpf) === cpf);

          const byHolderCpf =
            clone.find((life) => normalizeCpfKey(life.beneficiaryCpf) === holderCpf) ??
            clone.find((life) => normalizeCpfKey(life.holderCpf) === holderCpf);

          const byName = clone.find((life) => normalizeString(life.beneficiaryName) === nameKey);

          // Match by Holder Name (Exact)
          const byHolderName = holderNameKey
            ? clone.find((life) => normalizeString(life.beneficiaryName) === holderNameKey)
            : undefined;

          // Match by Cleaned Holder Name (ignoring trailing digits)
          const byCleanedHolderName = cleanedHolderNameKey
            ? clone.find((life) => normalizeString(life.beneficiaryName) === cleanedHolderNameKey)
            : undefined;

          // Partial Match: Check if beneficiary name is a prefix of life name or vice versa
          const byPartialName = nameKey
            ? clone.find(life => {
              const lifeName = normalizeString(life.beneficiaryName);
              return lifeName.startsWith(nameKey) || nameKey.startsWith(lifeName);
            })
            : undefined;

          const holderMatch =
            (holderCpf
              ? clone.find(
                (life) => normalizeCpfKey(life.beneficiaryCpf) === holderCpf && isHolderRecord(life),
              ) ??
              clone.find(
                (life) => normalizeCpfKey(life.holderCpf) === holderCpf && isHolderRecord(life),
              )
              : undefined) ??
            (entry.holderUserCode
              ? clone.find(
                (life) => normalizeCpfKey(life.userCode) === normalizeCpfKey(entry.holderUserCode) && isHolderRecord(life),
              )
              : undefined) ??
            (byHolderName && isHolderRecord(byHolderName) ? byHolderName : undefined) ??
            (byCleanedHolderName && isHolderRecord(byCleanedHolderName) ? byCleanedHolderName : undefined);

          if (holderMatch) return holderMatch;

          return byUserCode ?? byCpf ?? byHolderCpf ?? byName ?? byHolderName ?? byCleanedHolderName ?? byPartialName;
        };

        copays.forEach((entry) => {
          const target = findTarget(entry);
          if (!target) return;
          const value = entry.value ?? 0;
          target.copayAmount = (target.copayAmount ?? 0) + value;
          target.copayEntries = [...(target.copayEntries ?? []), entry];
        });

        return clone;
      },
    [],
  );

  type HealthPlanGroup = {
    key: string;
    holder: HealthPlanMatch | null;
    dependents: HealthPlanLifeRecord[];
  };

  const getPremiumValue = (record: HealthPlanLifeRecord) =>
    (typeof record.totalCost === 'number' && Number.isFinite(record.totalCost) ? record.totalCost : null) ??
    (typeof record.premiumTotal === 'number' && Number.isFinite(record.premiumTotal) ? record.premiumTotal : null) ??
    0;


  const buildMatches = (records: HealthPlanLifeRecord[]): HealthPlanMatch[] => {
    const indexByCpf = new Map(
      collaboratorRefs
        .filter((c) => c.cpf)
        .map((c) => [normalizeDigits(c.cpf), c]),
    );

    return records.map((record) => {
      const beneficiaryCpf = normalizeDigits(record.beneficiaryCpf);
      const holderCpf = normalizeDigits(record.holderCpf);
      const familyIdentifier = normalizeDigits(record.familyIdentifier);
      const certificateDigits = normalizeDigits(record.certificateCode);
      const userCodeDigits = normalizeDigits(record.userCode);
      const lookupCpf = beneficiaryCpf.length === 11 ? beneficiaryCpf : holderCpf.length === 11 ? holderCpf : beneficiaryCpf;
      const isHolder = isPrimaryHolderRecord(record);
      let savedByIdentifier: CollaboratorReference | undefined;
      if (isHolder) {
        const identifierCandidates = [userCodeDigits, familyIdentifier, certificateDigits].filter(
          (value): value is string => Boolean(value),
        );
        for (const candidate of identifierCandidates) {
          const match = collaboratorByIdentifier.get(candidate);
          if (match) {
            savedByIdentifier = match;
            break;
          }
        }
      }
      const normalizedName = normalizeString(record.beneficiaryName || '');
      const nameMatch = isHolder && normalizedName ? collaboratorByPlainName.get(normalizedName) : undefined;
      const cpfMatch = lookupCpf ? indexByCpf.get(lookupCpf) : undefined;

      const auto = savedByIdentifier ?? cpfMatch ?? nameMatch;
      return {
        record,
        collaboratorId: auto ? auto.id : null,
        status: auto ? 'auto_cpf' : 'not_found',
      };
    });
  };

  const handleParse = async () => {
    if (files.length === 0) return;
    setParsing(true);
    setParseError(null);
    setMatches([]);
    setTotals({});
    setMovements([]);
    setCopayEntries([]);
    setCopayRawText([]);

    try {
      const iofValue = manualIof ? parseFloat(manualIof.replace(',', '.')) : 0;

      const parsed = await parseHealthPlanFiles(selectedProvider, files, pdfPassword, iofValue);

      const livesWithCopay = parsed.copaysAttached
        ? parsed.lives
        : attachCopayToLives(parsed.lives, parsed.copayEntries);

      setMatches(buildMatches(livesWithCopay));
      setMovements(parsed.movements);
      setCopayEntries(parsed.copayEntries);
      setCopayRawText(parsed.debugRawPages ?? []);

      // Ensure totalCost uses the parser's value if available (it handles IOF properly now)
      // Only recalculate if we are messing with copays or premiums manually, but usually parser is authoritative.
      const copaySumLives = livesWithCopay.reduce((acc: number, life: HealthPlanLifeRecord) => acc + (life.copayAmount ?? 0), 0);
      const resolvedCopayTotal =
        Number.isFinite(copaySumLives) ? copaySumLives : parsed.totals?.copayTotal ?? null;

      setTotals({
        ...parsed.totals,
        copayTotal: resolvedCopayTotal,
        // Prefer the parser's totalCost because it knows about premiums + IOF.
        // If copays are significant and not included, we might need adjustments, but let's trust the parser.
        totalCost: parsed.totals?.totalCost,
      });
    } catch (err) {
      console.error(err);
      setParseError(err instanceof Error ? err.message : 'Erro ao processar arquivo.');
    } finally {
      setParsing(false);
    }
  };



  useEffect(() => {
    if (selectedProvider) return;
    setMatches([]);
    setMovements([]);
    setCopayEntries([]);
    setCopayRawText([]);
    setTotals({});
  }, [selectedProvider]);

  const groupedMatches: HealthPlanGroup[] = useMemo(() => {
    const order: Record<MatchStatus, number> = { not_found: 0, manual: 1, auto_cpf: 2 };
    const sorted = [...matches].sort((a, b) => order[a.status] - order[b.status]);

    const groups = new Map<string, HealthPlanGroup>();

    sorted.forEach((match, index) => {
      const key =
        normalizeDigits(match.record.familyIdentifier) ||
        normalizeDigits(match.record.holderCpf) ||
        normalizeDigits(match.record.beneficiaryCpf) ||
        `row-${index}`;
      const isHolder = isPrimaryHolderRecord(match.record);

      const current = groups.get(key) ?? { key, holder: null, dependents: [] };
      if (isHolder && !current.holder) {
        current.holder = match;
      } else {
        current.dependents.push(match.record);
      }
      groups.set(key, current);
    });
    const unsortedGroups = Array.from(groups.values()).map((group) => {
      if (!group.holder) {
        const holderIdx = group.dependents.findIndex((dep) => isPrimaryHolderRecord(dep));
        if (holderIdx >= 0) {
          const [holderRecord] = group.dependents.splice(holderIdx, 1);
          group.holder = {
            record: holderRecord,
            collaboratorId: holderRecord.collaboratorId ?? null,
            status: holderRecord.collaboratorId ? 'manual' : 'not_found',
          };
        }
      }
      return group;
    });
    const groupPriority = (group: HealthPlanGroup): number => {
      if (!group.holder) return 0;
      return order[group.holder.status] ?? 1;
    };
    return unsortedGroups.sort((a, b) => groupPriority(a) - groupPriority(b));
  }, [matches]);

  const familiesPreview = useMemo(() => {
    return groupedMatches.map((group) => {
      const holderRecord = group.holder?.record ?? null;
      const dependents = group.dependents ?? [];
      const totalPremium =
        (holderRecord ? getPremiumValue(holderRecord) : 0) +
        dependents.reduce((acc, dep) => acc + getPremiumValue(dep), 0);
      const totalCopay =
        (holderRecord?.copayAmount ?? 0) +
        dependents.reduce((acc, dep) => acc + (dep.copayAmount ?? 0), 0);
      const collaboratorId =
        group.holder?.collaboratorId ??
        holderRecord?.collaboratorId ??
        dependents.find((dep) => dep.collaboratorId)?.collaboratorId ??
        null;
      return {
        familyKey: group.key,
        collaboratorId,
        holder: holderRecord,
        dependents,
        totalPremium,
        totalCopay,
      };
    });
  }, [groupedMatches]);

  const handleMatchChange = (index: number, collaboratorId: string | null) => {
    setMatches((prev) =>
      prev.map((match, idx) =>
        idx === index
          ? {
            ...match,
            collaboratorId: collaboratorId ?? null,
            status: collaboratorId ? 'manual' : 'not_found',
          }
          : match,
      ),
    );
  };

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    setFiles(selected);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedProvider) {
      showError('Selecione o plano de saúde.');
      return;
    }
    if (!files.length || !sourceFiles.length) {
      showError('Selecione ao menos um arquivo para importar.');
      return;
    }
    const competence = billingPeriod.trim();
    if (!competence) {
      showError('Informe a competência (AAAA-MM).');
      return;
    }
    const consolidated = await isCompetenceConsolidated(competence);
    if (consolidated) {
      showError('Não é possível importar: competência já consolidada.');
      return;
    }
    if (!matches.length) {
      showError('Realize o parse e conferência antes de salvar.');
      return;
    }
    if (!user) {
      showError('É necessário estar autenticado para importar.');
      return;
    }

    const provider = selectedProvider as HealthPlanProvider;

    try {
      setSubmitting(true);

      const audit: HealthPlanAuditItem[] = matches
        .map((match): HealthPlanAuditItem | null => {
          if (!isPrimaryHolderRecord(match.record)) return null;

          if (match.status === 'not_found') {
            return {
              beneficiaryName: match.record.beneficiaryName,
              beneficiaryCpf: match.record.beneficiaryCpf || null,
              provider: selectedProvider,
              premium: match.record.premiumTotal,
              copay: match.record.copayAmount,
              reason: 'Não vinculado a colaborador',
              type: 'not_found',
            };
          }
          const collaborator = collaboratorRefs.find((c) => c.id === match.collaboratorId);
          if (collaborator && !collaborator.isActive) {
            return {
              beneficiaryName: match.record.beneficiaryName,
              beneficiaryCpf: match.record.beneficiaryCpf || null,
              provider: selectedProvider,
              premium: match.record.premiumTotal,
              copay: match.record.copayAmount,
              reason: 'Colaborador inativo',
              type: 'inactive',
            };
          }
          return null;
        })
        .filter((item): item is HealthPlanAuditItem => item !== null);

      const created = await createHealthPlanImport(
        provider,
        {
          billingPeriod: billingPeriod || null,
          sourceFiles,
          totals,
          familiesPreview,
          movementsPreview: movements,
          status: 'ready',
          audit,
        },
        user,
      );
      const holderAssignments = matches
        .filter((match) => match.collaboratorId && isPrimaryHolderRecord(match.record))
        .map((match) => ({
          collaboratorId: match.collaboratorId!,
          userCode: match.record.userCode ?? null,
          familyIdentifier:
            match.record.familyIdentifier ?? match.record.holderCpf ?? match.record.beneficiaryCpf ?? null,
          certificateCode: match.record.certificateCode ?? null,
        }));
      if (holderAssignments.length) {
        await saveHealthPlanIdentifierAssignments(provider, holderAssignments);
      }
      showSuccess('Importação iniciada. Aguarde o processamento.');
      navigate(`/expenses/benefits/health-plan${selectedProvider ? `?provider=${selectedProvider}` : ''}`);
    } catch (err) {
      console.error('[HealthPlanImportPage] erro ao iniciar import', err);
      showError('Não foi possível iniciar a importação.');
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Importar Fatura: {providerInfo?.name ?? 'Desconhecido'}
            </h1>
            <p className="text-sm text-gray-500">
              {billingPeriod ? `Competência: ${billingPeriod}` : 'Selecione o arquivo para identificar a competência'}
            </p>
          </div>
        </div>
      </div>



      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Plano de saúde</label>
            <select
              value={selectedProvider}
              onChange={(event) => setSelectedProvider((event.target.value as HealthPlanProvider) || '')}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Selecione um plano</option>
              {healthPlanProviders.map((providerOption) => (
                <option key={providerOption.slug} value={providerOption.slug}>
                  {providerOption.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {providerInfo?.description ?? 'Escolha a operadora correspondente aos arquivos enviados.'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Competência (AAAA-MM)</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(event) => setBillingPeriod(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Arquivos</label>
            <label className="mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:border-primary-400">
              <span className="inline-flex items-center gap-2">
                <CloudArrowUpIcon className="h-5 w-5 text-primary-600" aria-hidden />
                {files.length ? `${files.length} arquivo(s) selecionado(s)` : 'Selecionar arquivos (.xlsx, .pdf, .txt)'}
              </span>
              <input type="file" accept=".xlsx,.xls,.csv,.pdf,.txt" multiple className="hidden" onChange={handleFilesChange} />
            </label>
            <p className="mt-1 text-xs text-gray-500">Mensalidade (xlsx, pdf) e copay (pdf, csv, txt).</p>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Senha do PDF (opcional)</label>
              <input
                type="password"
                value={pdfPassword}
                onChange={(e) => setPdfPassword(e.target.value)}
                placeholder="Senha para abrir o PDF"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">IOF / Despesas Financeiras</label>
              <input
                type="text"
                value={manualIof}
                onChange={(e) => setManualIof(e.target.value)}
                placeholder="0,00"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleParse}
            disabled={parsing || loadingCollaborators}
            className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {parsing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Lendo arquivo...
              </>
            ) : (
              'Conferir'
            )}
          </button>
          <button
            type="submit"
            disabled={submitting || parsing || loadingCollaborators || matches.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Salvando
              </>
            ) : (
              <>
                Importar
                <ArrowRightIcon className="h-4 w-4" aria-hidden />
              </>
            )}
          </button>
        </div>
      </form>

      {parseError ? <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{parseError}</div> : null}

      {matches.length > 0 ? (
        <div className="space-y-3 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Vidas (prévia)</h2>
              <p className="text-sm text-gray-600">Revise beneficiários, plano e faça o match com colaboradores.</p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-gray-600">Titulares: {matches.length}</span>
              {totals.totalCredit != null && (
                <span className="text-green-600">
                  Crédito: {totals.totalCredit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  {totals.totalCreditCount ? ` em ${totals.totalCreditCount} lançamentos` : ''}
                </span>
              )}
              {totals.totalDebit != null && (
                <span className="text-red-600">
                  Débito: {totals.totalDebit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  {totals.totalDebitCount ? ` em ${totals.totalDebitCount} lançamentos` : ''}
                </span>
              )}
              {totals.financialCost != null && totals.financialCost > 0 && (
                <span className="text-orange-600">
                  Financ.: {totals.financialCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              )}

              <span className="text-gray-600">
                Prêmio:{' '}
                {totals.premiumHealth != null
                  ? totals.premiumHealth.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                  : '—'}
              </span>

              {totals.copayTotal != null && (
                <span className="text-gray-600">
                  Copay:{' '}
                  {totals.copayTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              )}

              <span className="text-gray-900 font-bold">
                Total:{' '}
                {totals.totalCost != null
                  ? totals.totalCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                  : '—'}
              </span>
            </div>
          </div>

          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Beneficiário</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">CPF / Código</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Plano</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Prêmio</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Copay</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Match</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Colaborador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {groupedMatches.map((group, groupIndex) => {
                  const holder = group.holder;
                  const holderRecord = holder?.record ?? group.dependents[0];
                  const dependentsPremium = group.dependents.reduce((acc, dep) => acc + (dep.premiumTotal ?? 0), 0);
                  const dependentsCopay = group.dependents.reduce((acc, dep) => acc + (dep.copayAmount ?? 0), 0);
                  const holderPremium = (holderRecord?.premiumTotal ?? 0) + dependentsPremium;
                  return (
                    <Fragment key={group.key ?? `group-${groupIndex}`}>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-semibold text-gray-900">{holderRecord?.beneficiaryName ?? 'Titular'}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-800">{formatCpfOrCode(holderRecord)}</td>
                        <td className="px-3 py-2 text-gray-800">
                          <span className="inline-block max-w-[12rem] truncate align-middle">
                            {holderRecord?.planName ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          {holderPremium != null && holderPremium !== 0
                            ? holderPremium.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          {(() => {
                            const holderCpf = holderRecord?.beneficiaryCpf?.replace(/\D/g, '') ?? '';
                            const nameKey = normalizeString(holderRecord?.beneficiaryName ?? '');
                            const holderMapValue =
                              (holderCpf ? copayByCpf.get(holderCpf) : undefined) ??
                              (nameKey ? copayByCpf.get(nameKey) : undefined) ??
                              (holderRecord.userCode ? copayByCpf.get(holderRecord.userCode) : undefined);
                            const fallbackHolderCopay = (holderRecord?.copayAmount ?? 0) + dependentsCopay;
                            const resolvedCopay =
                              holderMapValue != null && Math.abs(holderMapValue) > 0.0001
                                ? holderMapValue
                                : fallbackHolderCopay;
                            return resolvedCopay != null && Math.abs(resolvedCopay) > 0.0001
                              ? resolvedCopay.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                              : '—';
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          {holder ? (
                            <StatusBadge status={holder.status} />
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                              <XCircleIcon className="h-4 w-4" aria-hidden />
                              Titular não identificado
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {holder ? (
                            <MappingCombobox
                              value={holder.collaboratorId ?? null}
                              options={collaboratorRefs}
                              disabled={loadingCollaborators}
                              onChange={(id) => handleMatchChange(matches.indexOf(holder), id)}
                            />
                          ) : (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                        </td>
                      </tr>
                      {group.dependents.map((dep, depIndex) => (
                        <tr key={`${group.key}-dep-${depIndex}`}>
                          <td className="px-3 py-2">
                            <div className="pl-6">
                              <div className="font-semibold text-gray-900">{dep.beneficiaryName}</div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-800">{formatCpfOrCode(dep)}</td>
                          <td className="px-3 py-2 text-gray-800">
                            <span className="inline-block max-w-[12rem] truncate align-middle">
                              {holderRecord?.planName ?? ''}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-800"></td>
                          <td className="px-3 py-2 text-gray-800"></td>
                          <td className="px-3 py-2 text-xs text-gray-600"></td>
                          <td className="px-3 py-2 text-xs text-gray-500"></td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

    </div>
  );
}
